import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Tooltip as ChartTooltip,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
} from 'chart.js';
import { useMemo } from 'react';
import { Chart } from 'react-chartjs-2';
import { LuSun, LuSunrise, LuSunset } from 'react-icons/lu';

import Modal from '@/components/Modal/Modal';
import WidgetDetailChrome from '@/features/dashboard/components/WidgetDetailChrome';

import { WEATHER_COLOR_VARS } from './config';
import { compassFromDeg, formatDaylight, formatTimeUntil, type DaySummary, type HourSlot, type WeatherSnapshot } from './weatherDerive';
import WeatherIcon from './weatherIcons';

ChartJS.register(BarController, BarElement, CategoryScale, ChartTooltip, Legend, LinearScale, LineController, LineElement, PointElement);

interface Props {
  open: boolean;
  onClose: () => void;
  data?: WeatherSnapshot;
  lastUpdated?: Date | null;
  onRefresh?: () => void;
}

function fmtHour(ms: number): string {
  return new Date(ms).toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' });
}

export default function WeatherDetailModal({ open, onClose, data, lastUpdated, onRefresh }: Props) {
  if (!open || !data) return null;

  const sunEvent = data.nextSunEvent;
  const nowMs = Date.now();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Weather · ${data.city}`}
      description={data.now ? data.now.condition.label : undefined}
      size="lg"
    >
      <WidgetDetailChrome lastUpdated={lastUpdated} onRefresh={onRefresh} />

      {/* Current snapshot */}
      {data.now && (
        <div className="mb-6 flex items-center gap-4">
          <WeatherIcon iconKey={data.now.condition.iconKey} size="4rem" />
          <div className="flex-1">
            <div className="font-display text-5xl font-semibold text-(--text-primary) tabular-nums">{Math.round(data.now.tempC)}°</div>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-(--text-muted)">
              <span>Feels {Math.round(data.now.feelsLikeC)}°</span>
              <span>Humidity {Math.round(data.now.humidity)}%</span>
              <span>Wind {Math.round(data.now.windMps)} m/s</span>
            </div>
          </div>
        </div>
      )}

      {/* Sun block */}
      {data.today && (
        <div className="mb-6 rounded-lg border border-(--border-subtle) bg-(--bg-surface) p-3 text-sm">
          {sunEvent && (
            <div className="mb-2 flex items-center gap-2 text-(--text-primary)">
              <span aria-hidden="true" className="text-lg leading-none text-(--accent-warning)">
                {sunEvent.type === 'sunrise' ? <LuSunrise /> : <LuSunset />}
              </span>
              <span>
                Next {sunEvent.type} at <span className="tabular-nums">{fmtHour(sunEvent.ms)}</span>{' '}
                <span className="text-(--text-muted)">· in {formatTimeUntil(sunEvent.ms, nowMs)}</span>
              </span>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-(--text-muted)">
            <span className="inline-flex items-center gap-1">
              <LuSunrise aria-hidden="true" className="text-sm text-(--accent-warning)" />
              <span className="tabular-nums">{fmtHour(data.today.summary.sunriseMs)}</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <LuSunset aria-hidden="true" className="text-sm text-(--accent-warning)" />
              <span className="tabular-nums">{fmtHour(data.today.summary.sunsetMs)}</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <LuSun aria-hidden="true" className="text-sm text-(--accent-warning)" />
              <span>{formatDaylight(data.today.summary.daylightSec)}</span>
            </span>
          </div>
        </div>
      )}

      {/* Today / Tomorrow charts */}
      <div className="space-y-6">
        {data.today && data.today.hourly.length > 0 && (
          <WeatherDayChart title="Today" hourly={data.today.hourly} currentHourMs={alignToHour(nowMs)} />
        )}
        {data.tomorrow && data.tomorrow.hourly.length > 0 && (
          <WeatherDayChart title="Tomorrow" hourly={data.tomorrow.hourly} currentHourMs={null} />
        )}
      </div>

      {/* 7-day strip */}
      {data.week.length > 0 && (
        <div className="mt-6">
          <h3 className="mb-2 text-xs font-semibold tracking-wide text-(--text-muted) uppercase">7-day</h3>
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-7">
            {data.week.map((d) => (
              <DayCard key={d.dateMs} day={d} />
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}

function alignToHour(ms: number): number {
  const d = new Date(ms);
  d.setMinutes(0, 0, 0);
  return d.getTime();
}

// ─── Day card ────────────────────────────────────────────────────────────────

function DayCard({ day }: { day: DaySummary }) {
  return (
    <div className="rounded-md border border-(--border-subtle) bg-(--bg-surface) p-2 text-center">
      <div className="text-xs text-(--text-muted)">{day.weekdayShort}</div>
      <div className="my-1 flex justify-center">
        <WeatherIcon iconKey={day.condition.iconKey} size="1.75rem" />
      </div>
      <div className="font-display text-sm text-(--text-primary) tabular-nums">
        {Math.round(day.tempMaxC)}° / <span className="text-(--text-tertiary)">{Math.round(day.tempMinC)}°</span>
      </div>
      <div className="text-[10px] text-(--accent-info)">
        {day.precipSumMm.toFixed(1)} mm · {Math.round(day.precipChanceMax)}%
      </div>
      <div className="text-[10px] text-(--text-muted)">{Math.round(day.windMaxMps)} m/s</div>
    </div>
  );
}

// ─── Day chart ───────────────────────────────────────────────────────────────

function WeatherDayChart({ title, hourly, currentHourMs }: { title: string; hourly: HourSlot[]; currentHourMs: number | null }) {
  const resolvedColors = useMemo(() => {
    const style = getComputedStyle(document.documentElement);
    return {
      temp: style.getPropertyValue(WEATHER_COLOR_VARS.temp).trim() || '#fbbf24',
      precip: style.getPropertyValue(WEATHER_COLOR_VARS.precip).trim() || '#60a5fa',
      muted: style.getPropertyValue(WEATHER_COLOR_VARS.muted).trim() || '#94a3b8',
      focus: style.getPropertyValue(WEATHER_COLOR_VARS.focus).trim() || '#fff',
    };
  }, []);

  const chartData = useMemo(() => {
    const labels = hourly.map((s) => {
      const d = new Date(s.ts);
      const h = d.getHours();
      return h % 3 === 0 ? `${String(h).padStart(2, '0')}:00` : '';
    });
    const precipBorderColors = hourly.map(() => 'transparent');
    const precipBorderWidths = hourly.map(() => 0);
    return {
      labels,
      datasets: [
        {
          type: 'bar' as const,
          label: 'Precip (mm)',
          data: hourly.map((s) => s.precipMm),
          backgroundColor: `${resolvedColors.precip}80`,
          borderColor: precipBorderColors,
          borderWidth: precipBorderWidths,
          yAxisID: 'yPrecip',
          order: 3,
        },
        {
          type: 'line' as const,
          label: 'Feels (°C)',
          data: hourly.map((s) => s.feelsLikeC),
          borderColor: `${resolvedColors.temp}66`,
          backgroundColor: `${resolvedColors.temp}66`,
          pointRadius: 0,
          tension: 0.35,
          borderWidth: 1.5,
          borderDash: [3, 3],
          yAxisID: 'yTemp',
          order: 2,
        },
        {
          type: 'line' as const,
          label: 'Temp (°C)',
          data: hourly.map((s) => s.tempC),
          borderColor: resolvedColors.temp,
          backgroundColor: resolvedColors.temp,
          pointRadius: 0,
          tension: 0.35,
          borderWidth: 2,
          yAxisID: 'yTemp',
          order: 1,
        },
      ],
    };
  }, [hourly, resolvedColors]);

  const bandPlugin = useMemo(
    () => ({
      id: 'weatherAlternatingBands',
      beforeDatasetsDraw(chart: ChartJS) {
        const { ctx, chartArea, scales } = chart;
        const xScale = scales.x;
        if (!xScale || !chartArea) return;
        const count = hourly.length;
        if (count < 2) return;
        const step = xScale.getPixelForValue(1) - xScale.getPixelForValue(0) || (chartArea.right - chartArea.left) / count;
        ctx.save();
        ctx.fillStyle = `${resolvedColors.muted}14`;
        for (let i = 0; i < count; i += 2) {
          const center = xScale.getPixelForValue(i);
          const x1 = Math.max(chartArea.left, center - step / 2);
          const x2 = Math.min(chartArea.right, center + step / 2);
          ctx.fillRect(x1, chartArea.top, x2 - x1, chartArea.bottom - chartArea.top);
        }
        ctx.restore();
      },
    }),
    [hourly.length, resolvedColors.muted],
  );

  const currentHourPlugin = useMemo(
    () => ({
      id: 'weatherCurrentHour',
      beforeDatasetsDraw(chart: ChartJS) {
        if (currentHourMs == null) return;
        const { ctx, chartArea, scales } = chart;
        const xScale = scales.x;
        if (!xScale || !chartArea) return;
        const idx = hourly.findIndex((s) => s.ts === currentHourMs);
        if (idx < 0) return;
        const step = xScale.getPixelForValue(1) - xScale.getPixelForValue(0) || (chartArea.right - chartArea.left) / hourly.length;
        const center = xScale.getPixelForValue(idx);
        const x1 = Math.max(chartArea.left, center - step / 2);
        const x2 = Math.min(chartArea.right, center + step / 2);
        ctx.save();
        ctx.fillStyle = `${resolvedColors.focus}26`;
        ctx.fillRect(x1, chartArea.top, x2 - x1, chartArea.bottom - chartArea.top);
        ctx.strokeStyle = `${resolvedColors.focus}80`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(center + 0.5, chartArea.top);
        ctx.lineTo(center + 0.5, chartArea.bottom);
        ctx.stroke();
        ctx.restore();
      },
    }),
    [hourly, currentHourMs, resolvedColors.focus],
  );

  const windPlugin = useMemo(
    () => ({
      id: 'weatherWindStrip',
      afterDatasetsDraw(chart: ChartJS) {
        const { ctx, chartArea, scales } = chart;
        const xScale = scales.x;
        if (!xScale || !chartArea) return;
        const baseY = chartArea.top - 18;
        const textY = chartArea.top - 4;
        ctx.save();
        ctx.font = '10px system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'alphabetic';
        for (let i = 0; i < hourly.length; i++) {
          const s = hourly[i]!;
          const x = xScale.getPixelForValue(i);
          const calm = s.windMps < 1.5;
          const opacity = calm ? 0.5 : Math.min(1, 0.4 + s.windMps / 10);
          ctx.globalAlpha = opacity;
          ctx.strokeStyle = resolvedColors.precip;
          ctx.fillStyle = resolvedColors.precip;
          if (calm) {
            ctx.lineWidth = 1.2;
            ctx.beginPath();
            ctx.arc(x, baseY, 3, 0, Math.PI * 2);
            ctx.stroke();
          } else {
            ctx.save();
            ctx.translate(x, baseY);
            ctx.rotate(((s.windDirDeg + 180) * Math.PI) / 180);
            ctx.lineWidth = 1.3;
            ctx.beginPath();
            ctx.moveTo(0, -5);
            ctx.lineTo(0, 5);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(-3, 2);
            ctx.lineTo(0, 5);
            ctx.lineTo(3, 2);
            ctx.closePath();
            ctx.fill();
            ctx.restore();
          }
          ctx.fillText(String(Math.round(s.windMps)), x, textY);
        }
        ctx.restore();
      },
    }),
    [hourly, resolvedColors.precip],
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 24 } },
      interaction: { mode: 'index' as const, intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (ctx: { dataIndex: number }[]) => {
              const idx = ctx[0]?.dataIndex ?? 0;
              const s = hourly[idx];
              return s ? fmtHour(s.ts) : '';
            },
            afterBody: (ctx: { dataIndex: number }[]) => {
              const idx = ctx[0]?.dataIndex ?? 0;
              const s = hourly[idx];
              if (!s) return '';
              return `Wind: ${Math.round(s.windMps)} m/s ${compassFromDeg(s.windDirDeg)}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          border: { display: false },
          ticks: {
            color: resolvedColors.muted,
            font: { size: 10 },
            maxRotation: 0,
            autoSkip: false,
            callback: (_v: string | number, i: number) => {
              const s = hourly[i];
              if (!s) return '';
              const h = new Date(s.ts).getHours();
              return h % 2 === 0 ? String(h).padStart(2, '0') : '';
            },
          },
        },
        yTemp: {
          position: 'left' as const,
          title: { display: true, text: '°C', color: resolvedColors.muted, font: { size: 10 }, align: 'end' as const },
          grid: { color: `${resolvedColors.muted}33`, drawTicks: false },
          border: { display: false },
          ticks: { color: resolvedColors.muted, font: { size: 10 }, stepSize: 5, padding: 6 },
        },
        yPrecip: {
          position: 'right' as const,
          title: { display: true, text: 'mm', color: resolvedColors.muted, font: { size: 10 }, align: 'end' as const },
          grid: { display: false },
          border: { display: false },
          ticks: { color: resolvedColors.muted, font: { size: 10 }, padding: 6 },
          beginAtZero: true,
          suggestedMax: 2,
        },
      },
    }),
    [hourly, resolvedColors],
  );

  return (
    <div>
      <h3 className="mb-2 text-xs font-semibold tracking-wide text-(--text-muted) uppercase">{title}</h3>
      <div className="h-44">
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        <Chart type="bar" data={chartData as any} options={options as any} plugins={[bandPlugin, currentHourPlugin, windPlugin] as any} />
      </div>
    </div>
  );
}
